import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SeoHead from "@/components/feature/SeoHead";

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoTitle = "关于春夜影院 - 品牌介绍与联系方式";
  const seoDescription = "春夜影院是领先的免费高清在线观影平台，提供蓝光画质电影电视剧在线观看。了解春夜影院品牌故事、发展历程、企业信誉及联系方式。手机版、电脑版便捷访问。";
  const seoKeywords = "春夜影院,关于我们,品牌介绍,联系方式,免费高清电影,在线观看";

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于春夜影院",
    description: seoDescription,
    about: {
      "@type": "Organization",
      name: "春夜影院",
      description: "免费高清在线观影平台",
    },
  };

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://vxrboyl.cn/about"
        schema={aboutSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>

          {/* Hero */}
          <div className="bg-background-950 py-16">
            <div className="max-w-[800px] mx-auto text-center px-4">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-50 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                关于春夜影院
              </h1>
              <p className="text-foreground-400 text-sm leading-relaxed max-w-[600px] mx-auto">
                致力于为广大影迷提供免费、高清、流畅的在线观影体验，让每个人都能随时随地享受影院级视听盛宴。
              </p>
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="py-16 bg-background-50">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                品牌介绍
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500/10 flex items-center justify-center">
                    <i className="ri-eye-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-foreground-900 font-semibold mb-3">海量资源</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    收录数万部电影、电视剧、综艺和动漫资源，涵盖动作、喜剧、剧情、科幻、恐怖等多种类型，满足不同观众的观影需求。
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-500/10 flex items-center justify-center">
                    <i className="ri-hd-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="text-foreground-900 font-semibold mb-3">蓝光画质</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    所有影片均支持蓝光1080P高清画质，部分热门影片提供4K超清画质。采用先进的视频编码技术，播放流畅不缓冲。
                  </p>
                </div>

                <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-500/10 flex items-center justify-center">
                    <i className="ri-smartphone-line text-2xl text-secondary-500"></i>
                  </div>
                  <h3 className="text-foreground-900 font-semibold mb-3">多端适配</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    完美适配电脑、手机和平板等各类设备。响应式设计自动匹配屏幕尺寸，随时随地享受流畅观影体验。
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                <h3 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  我们的故事
                </h3>
                <div className="text-foreground-600 text-sm leading-relaxed space-y-4">
                  <p>
                    春夜影院创立于2023年，由一群热爱电影的年轻人共同打造。我们深知在快节奏的现代生活中，人们需要一个随时随地可以享受优质影视内容的平台。因此，我们决定创建一个完全免费、无需注册、操作简单的在线影院。
                  </p>
                  <p>
                    从最初的几百部影片起步，到今天拥有数万部高清影视资源，春夜影院始终坚持"免费、高清、流畅"的核心理念。我们采用先进的CDN加速技术，确保用户无论身在何处，都能享受流畅不缓冲的观影体验。
                  </p>
                  <p>
                    2025年，春夜影院完成全面升级，新增蓝光画质专区、手机版优化和个性化推荐功能。我们持续扩充内容库，每日更新最新上映的热门影片，力求成为用户最信赖的在线观影平台。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-16 bg-background-100">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                用户口碑
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    "画面清晰流畅，资源更新很快，基本上新片出来第二天就能看到。已经推荐给身边所有朋友了！"
                  </p>
                  <p className="text-foreground-500 text-xs">—— 电影爱好者 张先生</p>
                </div>

                <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    "手机版体验非常好，上下班路上追剧很方便。画质清晰不卡顿，是我用过最好的免费影院。"
                  </p>
                  <p className="text-foreground-500 text-xs">—— 追剧达人 李女士</p>
                </div>

                <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-half-line"></i>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    "分类清晰，找片方便。蓝光画质确实给力，用大屏电视看效果特别好。希望继续保持更新速度！"
                  </p>
                  <p className="text-foreground-500 text-xs">—— 影视发烧友 王先生</p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-16 bg-background-50">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-primary-500 mb-2">30000+</div>
                  <p className="text-foreground-500 text-sm">影视资源</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent-500 mb-2">500万+</div>
                  <p className="text-foreground-500 text-sm">月活跃用户</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-secondary-500 mb-2">99.9%</div>
                  <p className="text-foreground-500 text-sm">播放成功率</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-500 mb-2">24h</div>
                  <p className="text-foreground-500 text-sm">内容更新周期</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-16 bg-background-100">
            <div className="max-w-[700px] mx-auto px-4 md:px-6">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>

              <div className="bg-background-50 rounded-lg p-8 border border-background-200/70">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-900 font-medium text-sm mb-1">电子邮箱</h4>
                      <p className="text-foreground-500 text-sm">contact@chunyemovie.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-accent-500"></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-900 font-medium text-sm mb-1">官方网站</h4>
                      <p className="text-foreground-500 text-sm">vxrboyl.cn</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-secondary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-secondary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-900 font-medium text-sm mb-1">客服时间</h4>
                      <p className="text-foreground-500 text-sm">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-message-2-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-900 font-medium text-sm mb-1">问题反馈</h4>
                      <p className="text-foreground-500 text-sm">
                        如果您在使用过程中遇到任何问题，或有任何建议，欢迎随时通过邮箱与我们联系。我们会尽快回复并解决您的问题。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-12 bg-background-950">
            <div className="max-w-[800px] mx-auto text-center px-4">
              <h2 className="text-2xl font-bold text-foreground-50 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                加入春夜影院，畅享光影世界
              </h2>
              <p className="text-foreground-400 mb-8 text-sm">免费、高清、流畅，随时随地享受影院级观影体验</p>
              <Link
                to="/category/all"
                className="inline-flex items-center px-8 py-3 bg-primary-500 hover:bg-primary-600 text-foreground-50 font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm hover:scale-105"
              >
                <i className="ri-play-circle-line mr-2 text-lg"></i>立即观影
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}